import { nip19 } from 'nostr-tools';
import type { NostrEvent } from '@nostrify/nostrify';

/** Addressable kind for Locket Album manifests. */
export const ALBUM_KIND = 32981;
export const ALBUM_ALT = 'Encrypted photo/video album manifest (Locket Album)';

export interface AlbumFileEntry {
  url: string;
  sha256: string;
  nonce: string;
  mime: string;
  size: number;
  name: string;
  /** Encrypted thumbnail (videos): blob URL + its AES-GCM nonce. */
  thumbUrl?: string;
  thumbNonce?: string;
}

export interface SubAlbumRef {
  d: string;
  name: string;
}

export interface AlbumManifest {
  d: string;
  title: string;
  salt: string;
  cover?: string;
  files: AlbumFileEntry[];
  subs: SubAlbumRef[];
  parent?: string;
  /** Master key, NIP-44-encrypted by the owner to themselves. */
  encryptedKey?: string;
  event: NostrEvent;
}

function tagValue(event: NostrEvent, name: string): string | undefined {
  return event.tags.find(([n]) => n === name)?.[1];
}

const VIDEO_EXTS = ['mp4', 'webm', 'mov', 'm4v', 'mkv', 'avi', '3gp', 'ts'];
const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif', 'heic', 'heif', 'bmp', 'svg'];

const EXT_MIME: Record<string, string> = {
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  webm: 'video/webm',
  mov: 'video/quicktime',
  mkv: 'video/x-matroska',
  avi: 'video/x-msvideo',
  '3gp': 'video/3gpp',
  ts: 'video/mp2t',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  avif: 'image/avif',
  heic: 'image/heic',
  heif: 'image/heif',
  bmp: 'image/bmp',
  svg: 'image/svg+xml',
};

function extOf(name: string): string {
  return name.split('.').pop()?.toLowerCase() ?? '';
}

/** Guess a MIME type from the file extension (used when the browser reports none). */
export function sniffMimeByName(name: string): string {
  return EXT_MIME[extOf(name)] ?? 'application/octet-stream';
}

/** True if a file looks like a photo or video, by MIME type or extension. */
export function isMediaFile(file: File): boolean {
  if (file.type.startsWith('image/') || file.type.startsWith('video/')) return true;
  const ext = extOf(file.name);
  return IMAGE_EXTS.includes(ext) || VIDEO_EXTS.includes(ext);
}

/** True if a manifest entry is a video, even if its stored MIME type is missing/wrong. */
export function isVideoEntry(entry: Pick<AlbumFileEntry, 'mime' | 'name'>): boolean {
  if (entry.mime.startsWith('video/')) return true;
  if (entry.mime.startsWith('image/')) return false;
  return VIDEO_EXTS.includes(extOf(entry.name));
}

/** A browser-playable MIME type for the decrypted blob. */
export function playableMime(entry: Pick<AlbumFileEntry, 'mime' | 'name'>): string {
  if (entry.mime && entry.mime !== 'application/octet-stream') return entry.mime;
  return sniffMimeByName(entry.name);
}

export function parseAlbumEvent(event: NostrEvent): AlbumManifest | undefined {
  if (event.kind !== ALBUM_KIND) return undefined;
  const d = tagValue(event, 'd');
  const salt = tagValue(event, 'salt');
  if (!d || !salt) return undefined;

  const files: AlbumFileEntry[] = [];
  for (const t of event.tags) {
    if (t[0] === 'file' && t[1] && t[2] && t[3] && t[4]) {
      files.push({
        url: t[1],
        sha256: t[2],
        nonce: t[3],
        mime: t[4],
        size: Number(t[5] ?? '0') || 0,
        name: t[6] ?? 'file',
        thumbUrl: t[7] || undefined,
        thumbNonce: t[8] || undefined,
      });
    }
  }

  const subs: SubAlbumRef[] = event.tags
    .filter((t) => t[0] === 'sub' && t[1])
    .map((t) => ({ d: t[1], name: t[2] ?? 'Sub-album' }));

  return {
    d,
    title: tagValue(event, 'title') ?? '',
    salt,
    cover: tagValue(event, 'cover'),
    files,
    subs,
    parent: tagValue(event, 'parent'),
    encryptedKey: tagValue(event, 'key'),
    event,
  };
}

/** Build the tag list for publishing an album manifest. */
export function manifestTags(m: Omit<AlbumManifest, 'event'>): string[][] {
  const tags: string[][] = [
    ['d', m.d],
    ['salt', m.salt],
    ['alt', ALBUM_ALT],
  ];
  if (m.title) tags.push(['title', m.title]);
  if (m.cover) tags.push(['cover', m.cover]);
  if (m.parent) tags.push(['parent', m.parent]);
  if (m.encryptedKey) tags.push(['key', m.encryptedKey]);
  for (const s of m.subs) tags.push(['sub', s.d, s.name]);
  for (const f of m.files) {
    const tag = ['file', f.url, f.sha256, f.nonce, f.mime, String(f.size), f.name];
    if (f.thumbUrl && f.thumbNonce) tag.push(f.thumbUrl, f.thumbNonce);
    tags.push(tag);
  }
  return tags;
}

/** Build a shareable album link. The key lives in the fragment and never reaches a server. */
export function albumLink(origin: string, pubkey: string, d: string, masterKeyB64u: string): string {
  const naddr = nip19.naddrEncode({ kind: ALBUM_KIND, pubkey, identifier: d });
  return `${origin}/a/${naddr}#k=${masterKeyB64u}`;
}

export function albumNaddr(pubkey: string, d: string): string {
  return nip19.naddrEncode({ kind: ALBUM_KIND, pubkey, identifier: d });
}

/** Extract the master key from a location hash like `#k=...`. */
export function keyFromHash(hash: string): string | undefined {
  const m = /(?:^#|#|&)k=([A-Za-z0-9_-]+)/.exec(hash);
  return m?.[1];
}

/** Deterministic hue (0-359) derived from an album id, used for the leaflet color. */
export function albumHue(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h % 360;
}

const KEY_STORE_PREFIX = 'locket-album-key:';

/** Persist a master key locally so the owner can reopen their albums on this device. */
export function saveAlbumKey(pubkey: string, d: string, masterKeyB64u: string): void {
  try {
    localStorage.setItem(KEY_STORE_PREFIX + pubkey + ':' + d, masterKeyB64u);
  } catch {
    // storage unavailable — ignore
  }
}

export function loadAlbumKey(pubkey: string, d: string): string | undefined {
  try {
    return localStorage.getItem(KEY_STORE_PREFIX + pubkey + ':' + d) ?? undefined;
  } catch {
    return undefined;
  }
}
